import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../axios';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Plus, Save, Play } from 'lucide-react';
import toast from 'react-hot-toast';

interface TierItem {
  rankFrom: number;
  rankTo: number;
  rewardAmount: number;
}

interface LeaderboardConfig {
  id: number;
  periodType: string;
  enabled: boolean;
  rewardEnabled: boolean;
  voteWeight: number;
  likeWeight: number;
  topN: number;
  tiers: TierItem[];
}

const PERIOD_LABELS: Record<string, string> = {
  WEEKLY: '주간 (통합)',
  WEEKLY_VOTE: '주간 참여 순위',
  WEEKLY_LIKE: '주간 좋아요 순위',
  MONTHLY: '월간',
  YEARLY: '연간',
};

export const LeaderboardManagement = () => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<LeaderboardConfig>>({});

  const { data: rawConfigs, isLoading } = useQuery({
    queryKey: ['adminLeaderboardConfigs'],
    queryFn: () => apiClient.get('/api/admin/leaderboard/config').then((res: any) => res.data),
  });
  const configs: LeaderboardConfig[] = Array.isArray(rawConfigs) ? rawConfigs : (rawConfigs?.results ?? []);

  const createMutation = useMutation({
    mutationFn: (data: Partial<LeaderboardConfig>) => apiClient.post('/api/admin/leaderboard/config', data),
    onSuccess: () => {
      toast.success('설정이 생성됐어요');
      queryClient.invalidateQueries({ queryKey: ['adminLeaderboardConfigs'] });
    },
    onError: () => toast.error('생성 실패'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<LeaderboardConfig> }) => apiClient.put(`/api/admin/leaderboard/config/${id}`, data),
    onSuccess: () => {
      toast.success('설정이 업데이트됐어요');
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ['adminLeaderboardConfigs'] });
    },
    onError: () => toast.error('업데이트 실패'),
  });

  const aggregateMutation = useMutation({
    mutationFn: () => apiClient.post('/api/admin/leaderboard/aggregate/weekly'),
    onSuccess: () => toast.success('주간 집계 완료'),
    onError: () => toast.error('집계 실패'),
  });

  const handleCreateWeekly = async () => {
    const defaultTiers = [
      { rankFrom: 1, rankTo: 1, rewardAmount: 10 },
      { rankFrom: 2, rankTo: 2, rewardAmount: 7 },
      { rankFrom: 3, rankTo: 3, rewardAmount: 5 },
      { rankFrom: 4, rankTo: 5, rewardAmount: 3 },
    ];
    try {
      await apiClient.post('/api/admin/leaderboard/config', {
        periodType: 'WEEKLY_VOTE',
        enabled: true,
        rewardEnabled: true,
        voteWeight: 1,
        likeWeight: 0,
        topN: 5,
        tiers: defaultTiers,
      });
      await apiClient.post('/api/admin/leaderboard/config', {
        periodType: 'WEEKLY_LIKE',
        enabled: true,
        rewardEnabled: true,
        voteWeight: 0,
        likeWeight: 1,
        topN: 5,
        tiers: defaultTiers,
      });
      toast.success('참여 순위 + 좋아요 순위 설정이 생성됐어요');
      queryClient.invalidateQueries({ queryKey: ['adminLeaderboardConfigs'] });
    } catch {
      toast.error('생성 실패');
    }
  };

  const startEdit = (config: LeaderboardConfig) => {
    setEditingId(config.id);
    setEditForm({ ...config });
  };

  const saveEdit = () => {
    if (!editingId || !editForm) return;
    updateMutation.mutate({ id: editingId, data: editForm });
  };

  if (isLoading) return <div className="p-4 text-gray-500">로딩 중...</div>;

  const hasWeekly = configs.some((c: LeaderboardConfig) => c.periodType.startsWith('WEEKLY'));

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">리더보드 관리</h2>
          <p className="text-sm text-gray-500 mt-1">주기별 랭킹 설정 및 보상 구간을 관리해요</p>
        </div>
        <div className="flex gap-2">
          {!hasWeekly && (
            <Button onClick={handleCreateWeekly} className="flex items-center gap-1.5" disabled={createMutation.isPending}>
              <Plus className="w-4 h-4" />
              주간 설정 생성
            </Button>
          )}
          <Button variant="outline" onClick={() => aggregateMutation.mutate()} disabled={aggregateMutation.isPending}>
            <Play className="w-4 h-4 mr-1.5" />
            수동 집계
          </Button>
        </div>
      </div>

      {configs.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-gray-500 mb-4">아직 리더보드 설정이 없어요</p>
          <Button onClick={handleCreateWeekly} disabled={createMutation.isPending}>
            <Plus className="w-4 h-4 mr-1.5" />
            주간 설정 생성하기
          </Button>
        </Card>
      ) : (
        configs.map(config => {
          const isEditing = editingId === config.id;
          return (
            <Card key={config.id} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold">{PERIOD_LABELS[config.periodType] || config.periodType} 리더보드</h3>
                  <Badge variant={config.enabled ? 'default' : 'secondary'}>
                    {config.enabled ? '활성' : '비활성'}
                  </Badge>
                  <Badge variant={config.rewardEnabled ? 'default' : 'secondary'}>
                    보상 {config.rewardEnabled ? 'ON' : 'OFF'}
                  </Badge>
                </div>
                {isEditing ? (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>취소</Button>
                    <Button size="sm" onClick={saveEdit} disabled={updateMutation.isPending}>
                      <Save className="w-3.5 h-3.5 mr-1" />
                      저장
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => startEdit(config)}>수정</Button>
                )}
              </div>

              {/* 설정값 */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
                <div>
                  <label className="text-xs text-gray-500">활성화</label>
                  {isEditing ? (
                    <select className="w-full border rounded px-2 py-1 text-sm" value={editForm.enabled ? 'true' : 'false'}
                      onChange={e => setEditForm(f => ({ ...f, enabled: e.target.value === 'true' }))}>
                      <option value="true">ON</option>
                      <option value="false">OFF</option>
                    </select>
                  ) : (
                    <p className="text-sm font-medium">{config.enabled ? 'ON' : 'OFF'}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500">보상 지급</label>
                  {isEditing ? (
                    <select className="w-full border rounded px-2 py-1 text-sm" value={editForm.rewardEnabled ? 'true' : 'false'}
                      onChange={e => setEditForm(f => ({ ...f, rewardEnabled: e.target.value === 'true' }))}>
                      <option value="true">ON</option>
                      <option value="false">OFF</option>
                    </select>
                  ) : (
                    <p className="text-sm font-medium">{config.rewardEnabled ? 'ON' : 'OFF'}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500">투표 가중치</label>
                  {isEditing ? (
                    <input type="number" className="w-full border rounded px-2 py-1 text-sm" value={editForm.voteWeight ?? 1}
                      onChange={e => setEditForm(f => ({ ...f, voteWeight: Number(e.target.value) }))} />
                  ) : (
                    <p className="text-sm font-medium">{config.voteWeight}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500">좋아요 가중치</label>
                  {isEditing ? (
                    <input type="number" className="w-full border rounded px-2 py-1 text-sm" value={editForm.likeWeight ?? 1}
                      onChange={e => setEditForm(f => ({ ...f, likeWeight: Number(e.target.value) }))} />
                  ) : (
                    <p className="text-sm font-medium">{config.likeWeight}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500">Top N</label>
                  {isEditing ? (
                    <input type="number" className="w-full border rounded px-2 py-1 text-sm" value={editForm.topN ?? 5}
                      onChange={e => setEditForm(f => ({ ...f, topN: Number(e.target.value) }))} />
                  ) : (
                    <p className="text-sm font-medium">{config.topN}명</p>
                  )}
                </div>
              </div>

              {/* 보상 구간 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-gray-500">보상 구간</label>
                  {isEditing && (
                    <Button size="sm" variant="outline" onClick={() => {
                      const tiers = [...(editForm.tiers ?? [])];
                      const lastTo = tiers.length > 0 ? tiers[tiers.length - 1].rankTo : 0;
                      tiers.push({ rankFrom: lastTo + 1, rankTo: lastTo + 1, rewardAmount: 1 });
                      setEditForm(f => ({ ...f, tiers }));
                    }}>
                      <Plus className="w-3 h-3 mr-1" />구간 추가
                    </Button>
                  )}
                </div>
                {isEditing ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1 text-gray-500 font-normal">시작</th>
                        <th className="text-left py-1 text-gray-500 font-normal">끝</th>
                        <th className="text-left py-1 text-gray-500 font-normal">보상(조각)</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(editForm.tiers ?? []).map((tier, i) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-1">
                            <input type="number" className="w-16 border rounded px-2 py-1 text-sm" value={tier.rankFrom}
                              onChange={e => {
                                const tiers = [...(editForm.tiers ?? [])];
                                tiers[i] = { ...tiers[i], rankFrom: Number(e.target.value) };
                                setEditForm(f => ({ ...f, tiers }));
                              }} />
                          </td>
                          <td className="py-1">
                            <input type="number" className="w-16 border rounded px-2 py-1 text-sm" value={tier.rankTo}
                              onChange={e => {
                                const tiers = [...(editForm.tiers ?? [])];
                                tiers[i] = { ...tiers[i], rankTo: Number(e.target.value) };
                                setEditForm(f => ({ ...f, tiers }));
                              }} />
                          </td>
                          <td className="py-1">
                            <input type="number" className="w-16 border rounded px-2 py-1 text-sm" value={tier.rewardAmount}
                              onChange={e => {
                                const tiers = [...(editForm.tiers ?? [])];
                                tiers[i] = { ...tiers[i], rewardAmount: Number(e.target.value) };
                                setEditForm(f => ({ ...f, tiers }));
                              }} />
                          </td>
                          <td className="py-1">
                            <button className="text-red-400 hover:text-red-600 text-xs" onClick={() => {
                              const tiers = (editForm.tiers ?? []).filter((_, idx) => idx !== i);
                              setEditForm(f => ({ ...f, tiers }));
                            }}>삭제</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : config.tiers && config.tiers.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1 text-gray-500 font-normal">순위</th>
                        <th className="text-right py-1 text-gray-500 font-normal">보상 (조각)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {config.tiers.map((tier, i) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-1.5">{tier.rankFrom === tier.rankTo ? `${tier.rankFrom}위` : `${tier.rankFrom}~${tier.rankTo}위`}</td>
                          <td className="py-1.5 text-right font-medium">{tier.rewardAmount}개</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-gray-400">보상 구간 없음</p>
                )}
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
};
